import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { PalletVisualization } from '../PalletVisualization';
import { OptimizationResult, PackedPallet, CartonPosition } from "@/lib/autoPatternOptimization";
import { visualConfig } from '../../config/visualConfig';

interface ResultsTabProps {
  optimizationResult: OptimizationResult | null;
  showVisualization: boolean;
  cartonQuantity: number;
  usePallets: boolean;
  cartonLength: number;
  cartonWidth: number;
  cartonHeight: number;
  containerLength: number;
  containerWidth: number;
  containerHeight: number;
  containerWeightCapacity: number;
  goToPreviousTab: () => void;
}

export function ResultsTab({
  optimizationResult,
  showVisualization,
  cartonQuantity,
  usePallets,
  cartonLength,
  cartonWidth,
  cartonHeight,
  containerLength,
  containerWidth,
  containerHeight,
  containerWeightCapacity,
  goToPreviousTab
}: ResultsTabProps) {
  const colors = visualConfig.colors.results;
  
  return (
    <Card className={`${visualConfig.layout.card.minHeight} flex flex-col ${visualConfig.layout.card.background} ${visualConfig.layout.card.shape} overflow-hidden`}>
      <CardHeader className={`bg-gradient-to-r ${colors.gradient} text-white ${visualConfig.layout.header.padding}`}>
        <CardTitle className={visualConfig.layout.header.title}>Optimization Results</CardTitle>
        <CardDescription className={`text-emerald-100 ${visualConfig.layout.header.description}`}>
          Results from the last optimization calculation.
        </CardDescription>
        {/* Mode section commented out - no other modes currently available
        {optimizationResult && (
          <div className="mt-4 p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
            <div className="flex items-center justify-between">
              <span className={`${visualConfig.typography.small} font-semibold text-white`}>
                Mode: Specific Quantity
              </span>
              <span className={`${visualConfig.typography.tiny} text-emerald-100 bg-white/20 px-2 py-1 rounded-lg`}>
                Optimized for {cartonQuantity} cartons
              </span>
            </div>
          </div>
        )}
        */}
      </CardHeader>
      <CardContent className={visualConfig.layout.content.padding}>
        {!optimizationResult ? (
          <div className="text-center py-20">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-gray-200 rounded-full flex items-center justify-center mb-6">
                <span className="text-4xl">📦</span>
              </div>
              <p className={`${visualConfig.colors.neutral.text.muted} ${visualConfig.typography.sectionTitle} mb-4 font-medium`}>No results available</p>
              <p className={`${visualConfig.colors.neutral.text.disabled} ${visualConfig.typography.body}`}>Click "Optimize Loading" to generate results</p>
            </div>
          </div>
                  ) : optimizationResult.error ? (
            // Display validation errors
            <div className="max-w-4xl mx-auto">
              <div className={`p-6 ${visualConfig.components.alerts.error}`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className={`${visualConfig.typography.subsectionTitle} text-red-800 mb-3`}>
                      Input Validation Errors
                    </h3>
                    <div className={`text-red-700 whitespace-pre-line ${visualConfig.typography.body} leading-relaxed`}>
                      {optimizationResult.error}
                    </div>
                    <div className="mt-4 p-4 bg-white/80 rounded-xl border border-red-200">
                      <p className={`${visualConfig.typography.small} text-red-600 font-medium`}>
                        💡 <strong>How to fix:</strong>
                      </p>
                      <ul className={`${visualConfig.typography.small} text-red-600 mt-2 space-y-1 ml-4`}>
                        <li>• Check that all dimension values are positive numbers</li>
                        <li>• Ensure carton dimensions fit within pallet/container dimensions</li>
                        <li>• Verify that quantities are whole numbers (1 or greater)</li>
                        <li>• Make sure all weight values are positive</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        ) : (
          <div className="space-y-2 sm:space-y-4">
            {/* Responsive Layout: Container breakdown under packing summary on wide screens */}
            <div className="flex flex-col lg:flex-row gap-2 sm:gap-4">
              {/* Left Column: Packing Summary + Container Breakdown */}
              <div className="flex-1 space-y-2 sm:space-y-4">
                {/* Packing Summary */}
                <div className="p-2 sm:p-3 md:p-4 bg-emerald-50/70 rounded-lg sm:rounded-xl">
                  <h3 className={`${visualConfig.typography.sectionTitle} mb-1 sm:mb-2 text-emerald-800`}>
                    Packing Summary
                  </h3>
                  <div className="grid grid-cols-1 gap-1 sm:gap-2">
                    <div className="bg-white p-2 sm:p-3 rounded-md sm:rounded-lg">
                      <p className={`${visualConfig.typography.small} text-gray-600 mb-1`}>Cartons Packed</p>
                      <p className={`${visualConfig.components.results.metric}`}>
                        {Math.min(optimizationResult.totalCartonsPacked, cartonQuantity)}
                      </p>
                      <p className="text-xs text-gray-500">of {cartonQuantity} requested</p>
                    </div>
                  </div>
                </div>

                {/* Warnings and Info Messages */}
                <div className="space-y-1 sm:space-y-2">
                  {/* Weight Warning - Ultra compact */}
                  {optimizationResult.weightWarning && (
                    <div className="p-1.5 sm:p-2 bg-red-50 border border-red-200 rounded-md sm:rounded-lg">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-3 w-3 sm:h-4 sm:w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-1.5 sm:ml-2">
                          <p className={`${visualConfig.typography.tiny} text-red-800`}>{optimizationResult.weightWarning}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pattern Advantages - Ultra compact */}
                  {optimizationResult.patternAdvantages && optimizationResult.patternAdvantages.length > 0 && (
                    <div className="p-1.5 sm:p-2 bg-blue-50 border border-blue-200 rounded-md sm:rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-1.5 sm:ml-2">
                          <h3 className={`${visualConfig.typography.tiny} font-medium text-blue-800`}>
                            {optimizationResult.selectedPattern.charAt(0).toUpperCase() + optimizationResult.selectedPattern.slice(1)} Pattern Advantages
                          </h3>
                          <div className="mt-2">
                            <ul className="text-sm text-blue-700 space-y-1">
                              {optimizationResult.patternAdvantages.map((advantage, index) => (
                                <li key={index} className="flex items-center">
                                  <span className="text-blue-500 mr-2">•</span>
                                  {advantage}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Container Breakdown - Hidden on mobile lg, shown below on narrow screens */}
                <div className="hidden lg:block">
                  {(() => {
                    if (!optimizationResult.packedContainers || optimizationResult.packedContainers.length === 0) {
                      return <p className={`text-gray-500 ${visualConfig.typography.body}`}>No container data available</p>;
                    }
                    
                    // Show actual container data from the optimization result
                    const containerResults: Array<{
                      description: string;
                      cartons: number;
                      pallets: number;
                    }> = optimizationResult.packedContainers.map((container, index) => ({
                      description: `Container ${index + 1}`,
                      cartons: Array.isArray(container.contents) ? container.contents.length : 0,
                      pallets: usePallets && Array.isArray(container.contents) ? container.contents.length : 0
                    }));

                    return (
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className={`${visualConfig.typography.sectionTitle} text-gray-900`}>Container Breakdown</h3>
                        {containerResults.map((result, index) => (
                          <div key={index} className="p-2 sm:p-3 md:p-4 bg-gray-50/70 rounded-lg sm:rounded-xl">
                            <h4 className={`${visualConfig.typography.subsectionTitle} mb-1 sm:mb-2 text-gray-800`}>
                              {result.description}
                            </h4>
                            <div className="grid grid-cols-2 gap-1 sm:gap-2">
                              {usePallets && (
                                <div className="bg-white p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                                  <p className={`${visualConfig.typography.tiny} text-gray-600`}>Pallets</p>
                                  <p className={`${visualConfig.typography.subsectionTitle} text-green-600`}>{result.pallets}</p>
                                </div>
                              )}
                              <div className="bg-white p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                                <p className={`${visualConfig.typography.tiny} text-gray-600`}>Cartons</p>
                                <p className={`${visualConfig.typography.subsectionTitle} text-blue-600`}>{result.cartons}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Right Column: 3D Visualization */}
              {showVisualization && (
                <div className="flex-1 lg:max-w-sm">
                  <div className="p-2 sm:p-3 md:p-4 bg-gray-50/70 rounded-lg sm:rounded-xl">
                    <h3 className={`${visualConfig.typography.sectionTitle} mb-1 sm:mb-2 text-center text-gray-900`}>3D Visualization</h3>
                    <div className="w-full aspect-square max-w-sm mx-auto">
                      <PalletVisualization
                        pallets={usePallets
                          ? (optimizationResult.packedContainers[0]?.contents as PackedPallet[] || []).slice(0, 30) // Show up to 30 pallets (one full container layer)
                          : optimizationResult.packedContainers.slice(0, 1).map((container, index) => ({
                              palletDimensions: {
                                length: containerLength,
                                width: containerWidth,
                                height: containerHeight,
                                maxWeight: containerWeightCapacity
                              },
                              position: { x: 0, y: 0, z: 0 },  // Always position at origin for visualization
                              cartons: (container.contents as CartonPosition[] || []) // Show all cartons for accurate floor loading visualization
                            }))}
                        cartonDimensions={{ length: cartonLength, width: cartonWidth, height: cartonHeight }}
                        containerDimensions={{ length: containerLength, width: containerWidth, height: containerHeight }}
                        utilization={optimizationResult.spaceUtilization}
                        usePallets={usePallets}
                        className="h-full"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Container Breakdown - Visible on narrow screens (below 3D viz) */}
            <div className="block lg:hidden">
              {(() => {
                if (!optimizationResult.packedContainers || optimizationResult.packedContainers.length === 0) {
                  return <p className={`text-gray-500 ${visualConfig.typography.body}`}>No container data available</p>;
                }
                
                // Show actual container data from the optimization result
                const containerResults: Array<{
                  description: string;
                  cartons: number;
                  pallets: number;
                }> = optimizationResult.packedContainers.map((container, index) => ({
                  description: `Container ${index + 1}`,
                  cartons: Array.isArray(container.contents) ? container.contents.length : 0,
                  pallets: usePallets && Array.isArray(container.contents) ? container.contents.length : 0
                }));

                return (
                  <div className="space-y-1 sm:space-y-2">
                    <h3 className={`${visualConfig.typography.sectionTitle} text-gray-900`}>Container Breakdown</h3>
                    {containerResults.map((result, index) => (
                      <div key={index} className="p-2 sm:p-3 md:p-4 bg-gray-50/70 rounded-lg sm:rounded-xl">
                        <h4 className={`${visualConfig.typography.subsectionTitle} mb-1 sm:mb-2 text-gray-800`}>
                          {result.description}
                        </h4>
                        <div className="grid grid-cols-2 gap-1 sm:gap-2">
                          {usePallets && (
                            <div className="bg-white p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                              <p className={`${visualConfig.typography.tiny} text-gray-600`}>Pallets</p>
                              <p className={`${visualConfig.typography.subsectionTitle} text-green-600`}>{result.pallets}</p>
                            </div>
                          )}
                          <div className="bg-white p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                            <p className={`${visualConfig.typography.tiny} text-gray-600`}>Cartons</p>
                            <p className={`${visualConfig.typography.subsectionTitle} text-blue-600`}>{result.cartons}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </CardContent>
      <div className={`${visualConfig.layout.footer.layout} ${visualConfig.layout.footer.padding}`}>
        <button
          onClick={goToPreviousTab}
          className={`${visualConfig.interactive.button.base} ${visualConfig.colors.neutral.previous} ${visualConfig.interactive.button.focus} focus:ring-gray-200`}
        >
          Previous
        </button>
        <div></div>
      </div>
    </Card>
  );
} 